import { CdkPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { interval, Observable, of, Subject } from 'rxjs';
import { delay, filter, map, retryWhen, scan, take, takeUntil, takeWhile } from 'rxjs/operators';
import * as _ from 'underscore';

import {
  selectActionBarPortal,
  selectForceActionBarLayoutClass,
} from '../store/action-bar-portal/action-bar-portal.selectors';
import { getDarkLogoSrc } from './helpers/dark-mode';
import { getSideMenuOpenedByDefault, isMobile } from './layout-utils';
import { ApplicationMenu, Module } from './models/menus.model';
import { IMenuByUserResponse } from './models/menu-by-user.model';
import { IApplication, IApplicationModules, IApplicationUser } from './models/application.model';
import { ParameterData } from './models/parameters.model';
import { IUserRoles } from './models/roles.model';
import { CoreArkikCommonService } from './services/arkik-common.service';
import { CoreDarkModeService } from './services/dark-mode.service';
import { CoreDisableNavItems } from './services/disable-nav-items.service';
import { CoreLandingFixService } from './services/landing-fix.service';
import { CoreScrollBusService } from './services/scroll-bus.service';
import { CoreSidenavAdminService } from './services/sidenav-admin.service';
import { LanguageSelector } from './layout.types';
import {
  CORE_LAYOUT_ACTION_BAR_PORTAL,
  CORE_LAYOUT_ALERT_SERVICE,
  CORE_LAYOUT_APP_REPOSITORY,
  CORE_LAYOUT_ARKIK_STATUS,
  CORE_LAYOUT_BOM_NOTIFICATIONS,
  CORE_LAYOUT_COMPONENTS_BY_ROLE,
  CORE_LAYOUT_DASHBOARD_MENU,
  CORE_LAYOUT_ENVIRONMENT,
  CORE_LAYOUT_MANAGE_IDB,
  CORE_LAYOUT_MENU_REPOSITORY,
  CORE_LAYOUT_MODAL_PORTAL,
  CORE_LAYOUT_PARAMETERS_DATA_API,
  CORE_LAYOUT_TABLE_CONFIG,
  CORE_LAYOUT_TRANSLATION_SERVICE,
  CORE_LAYOUT_UPDATE_COMMON_SERVICE,
  CoreLayoutEnvironment,
  ICoreLayoutActionBarPortalService,
  ICoreLayoutAlertService,
  ICoreLayoutAppRepository,
  ICoreLayoutArkikStatusService,
  ICoreLayoutBomNotificationsService,
  ICoreLayoutDashboardMenuService,
  ICoreLayoutManageIdbService,
  ICoreLayoutMenuRepository,
  ICoreLayoutModalPortalService,
  ICoreLayoutParametersDataApiService,
  ICoreLayoutTableConfigService,
  ICoreLayoutTranslationService,
  ICoreLayoutUpdateCommonService,
} from './tokens';

const MAX_RETRIES = 1;
const TIME_DELAY = 30000;
const IDB_USER_ROLES = 'UserRoles';

@Component({
  selector: 'arkik-core-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class CoreLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('actionBarPortalOutlet') actionBarPortalOutlet: CdkPortalOutlet;
  actionBarPortal!: TemplatePortal;
  private readonly _destroy$ = new Subject<void>();

  private user: any;
  imagePath = '';
  private applicationModules: IApplicationModules[] = [];
  private allApplicationModules: Map<string, IApplicationModules[]> = new Map<string, IApplicationModules[]>();
  private applications: IApplication[];
  private userRoles: IUserRoles[];
  private haveMenuUsersPermission = false;

  public readonly loaderTimeOut = 99999;
  company: any;
  public openMenu = true;
  public menuItemsDisabled = false;

  public iconAlert = 'accepted-ok';
  public msgAlert = '';
  public cancelButtonText = '';
  public confirmButtonText = '';
  public colorIconAlert = 'bright-green';
  public callbackCloseAlertModal: any;
  public currentItem: ApplicationMenu;
  public showAlert = false;

  public showLinks = false;
  public DCPL_COM_MOD = false;

  public readonly FADE_IN_DELAY = 60;
  public removePadding$: Observable<boolean> = this.dashboardMenuService
    ? this.dashboardMenuService.removePadding$
    : of(false);

  public heightOveraly = '220px';
  public responseMenusUser: IMenuByUserResponse;
  public applicationMenu: ApplicationMenu;
  public languageCode = 'ES';
  public isDarkModeEnabled = false;
  public isDarkMode = false;
  haveImage = false;
  photo = '';
  fullName = '';
  email = '';
  logoCompany = '';
  companyName = '';
  public isSupportUser = false;
  public inConstructionPumping: boolean;
  Logo: string;
  LogoDark: string;

  readonly today = new Date();
  tableActive: Observable<boolean> = this.tableConfigService
    ? this.tableConfigService.tableActive.asObservable()
    : of(false);
  public bomBadgeLabel$ = this.bomNotificationsService ? this.bomNotificationsService.badgeLabel$ : of('');
  forceActionBarLayout: boolean;

  private onOpenTabKey: string;

  @ViewChild('modalAlert') modalAlert: TemplateRef<unknown>;
  @ViewChild('alertHost', { read: ViewContainerRef, static: true }) alertHost!: ViewContainerRef;
  @ViewChild('layoutPage') layoutPage: any;

  public languages: LanguageSelector[] = [
    { id: 'es-mx', value: 'ES', selected: false, pipeId: 'es' },
    { id: 'en-us', value: 'EN', selected: false, pipeId: 'en' },
  ];

  constructor(
    private viewContainerRef: ViewContainerRef,
    public router: Router,
    private cookieService: CookieService,
    private cdr: ChangeDetectorRef,
    private fix: CoreLandingFixService,
    private disableNavItems: CoreDisableNavItems,
    private darkModeService: CoreDarkModeService,
    private arkCommon: CoreArkikCommonService,
    private uxSideNavAdminService: CoreSidenavAdminService,
    private readonly cwcScrollBusService: CoreScrollBusService,
    @Optional() @Inject(CORE_LAYOUT_ENVIRONMENT) private env: CoreLayoutEnvironment,
    @Optional() @Inject(CORE_LAYOUT_COMPONENTS_BY_ROLE) private componentsByRole: any,
    @Optional() @Inject(CORE_LAYOUT_TRANSLATION_SERVICE) private ts: ICoreLayoutTranslationService,
    @Optional() @Inject(CORE_LAYOUT_UPDATE_COMMON_SERVICE) private updateCommonService: ICoreLayoutUpdateCommonService,
    @Optional() @Inject(CORE_LAYOUT_MENU_REPOSITORY) private menuRepository: ICoreLayoutMenuRepository,
    @Optional() @Inject(CORE_LAYOUT_APP_REPOSITORY) private applicationRepository: ICoreLayoutAppRepository,
    @Optional() @Inject(CORE_LAYOUT_DASHBOARD_MENU) private dashboardMenuService: ICoreLayoutDashboardMenuService,
    @Optional() @Inject(CORE_LAYOUT_MODAL_PORTAL) public portal: ICoreLayoutModalPortalService,
    @Optional() @Inject(CORE_LAYOUT_ARKIK_STATUS) public arkikStatus: ICoreLayoutArkikStatusService,
    @Optional()
    @Inject(CORE_LAYOUT_BOM_NOTIFICATIONS)
    private readonly bomNotificationsService: ICoreLayoutBomNotificationsService,
    @Optional() @Inject(CORE_LAYOUT_TABLE_CONFIG) private readonly tableConfigService: ICoreLayoutTableConfigService,
    @Optional() @Inject(CORE_LAYOUT_PARAMETERS_DATA_API) private parametersDataApi: ICoreLayoutParametersDataApiService,
    @Optional() @Inject(CORE_LAYOUT_ALERT_SERVICE) private readonly arkikAlertService: ICoreLayoutAlertService,
    @Optional() @Inject(CORE_LAYOUT_MANAGE_IDB) private idb: ICoreLayoutManageIdbService,
    @Optional()
    @Inject(CORE_LAYOUT_ACTION_BAR_PORTAL)
    private actionBarPortalService: ICoreLayoutActionBarPortalService,
    @Optional() private store: Store,
    @Optional() private authServiceB2C: MsalService,
  ) {
    const selected = localStorage.getItem('lang');
    this.disableNavItems.data$.subscribe((data: boolean) => (this.menuItemsDisabled = data));
    this.languages.forEach((language) => {
      if (language.pipeId === selected) {
        language.selected = true;
        this.languageCode = language.value;
        sessionStorage.setItem('languageCode', this.languageCode);
      }
    });

    if (isMobile()) this.uxSideNavAdminService.setOpen(false);
    this.onOpenTabKey = this.cookieService.get('user-email');
  }

  ngAfterViewInit(): void {
    this._setAlertContainer();
    this._setLayoutPageOverflow();
  }

  ngOnInit(): void {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.forEach((value, key) => {
      if (key === 'prms') {
        const values = this.arkCommon.decryptJSONObject(value, this.onOpenTabKey);
        Object.keys(values).forEach((ky: any) => sessionStorage.setItem(ky, values[ky]));
      }
    });

    this.setLogos();
    this.getParams();

    if (!sessionStorage.getItem('ApplicationGuid')) {
      this.router.navigate(['/home']);
    }

    this.fix.addFix();
    this.updateCommonService?.UpdateProfileData();
    this._subscribeCompanyData();
    this._subscribeProfileData();
    this.getApplicationsMenu();
    this.getUserRoles();

    this.darkModeService.darkMode$
      .pipe(takeUntil(this._destroy$))
      .subscribe((darkMode) => (this.isDarkMode = darkMode));
    this.darkModeService.isEnable$
      .pipe(takeUntil(this._destroy$))
      .subscribe((isEnable) => (this.isDarkModeEnabled = isEnable));

    this._syncSidenavOpenState();
    this._initActionBarPortal();
  }

  ngOnDestroy(): void {
    this.Logo = '';
    this.LogoDark = '';
    this.fix.removeFix();
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _setLayoutPageOverflow(): void {
    const page = this.layoutPage;
    if (!page?.el?.shadowRoot?.lastChild) return;

    setTimeout(() => {
      const nodeList: NodeList[] = page.el.shadowRoot.lastChild.childNodes;
      const nodeTarget: any = nodeList[1];
      const childNodes: any = nodeTarget.childNodes[1];
      childNodes.style.overflow = 'auto';
      childNodes.childNodes[0].style.overflow = 'auto';
    }, 1000);
  }

  private _syncSidenavOpenState(): void {
    this.uxSideNavAdminService.open$
      .pipe(
        scan((acc, isOpened) => ({ count: acc.count + 1, isOpened }), { count: 0, isOpened: false }),
        takeUntil(this._destroy$),
      )
      .subscribe(({ count, isOpened }) => {
        this.openMenu = count === 1 ? isMobile() || getSideMenuOpenedByDefault(this.router.url) || isOpened : isOpened;
        this.dashboardMenuService?.menuToogleBehavior(this.openMenu);
      });
  }

  private _initActionBarPortal(): void {
    if (this.store) {
      this.store
        .select(selectActionBarPortal)
        .pipe(takeUntil(this._destroy$))
        .subscribe((portal) => {
          this.actionBarPortal = portal;
          this.cdr.detectChanges();
        });
      this.store
        .select(selectForceActionBarLayoutClass)
        .pipe(takeUntil(this._destroy$))
        .subscribe((forceLayoutClass) => {
          this.forceActionBarLayout = forceLayoutClass;
          this.cdr.detectChanges();
        });
      return;
    }

    const actionBarPortalService = this.actionBarPortalService;
    if (!actionBarPortalService) return;

    actionBarPortalService.portal$.pipe(takeUntil(this._destroy$)).subscribe((portal) => {
      this.actionBarPortal = portal;
      this.cdr.detectChanges();
    });

    actionBarPortalService.forceLayoutClass$.pipe(takeUntil(this._destroy$)).subscribe((forceLayoutClass) => {
      this.forceActionBarLayout = forceLayoutClass;
      this.cdr.detectChanges();
    });
  }

  private _setAlertContainer(): void {
    if (!this.arkikAlertService) return;
    interval(300)
      .pipe(
        filter(() => this.alertHost !== undefined),
        takeWhile(() => !this.alertHost, true),
        takeUntil(this._destroy$),
      )
      .subscribe(() => this.arkikAlertService.setContainer(this.alertHost));
  }

  private _subscribeCompanyData(): void {
    this.updateCommonService?.companyData$.pipe(takeUntil(this._destroy$)).subscribe((companyData) => {
      this.company = companyData;
      this.logoCompany = companyData.Image ?? '';
      this.companyName = companyData.CompanyName ?? '';
    });
  }

  private _subscribeProfileData(): void {
    this.updateCommonService?.profileData$.pipe(takeUntil(this._destroy$)).subscribe((profileData) => {
      this.user = profileData;
      this.imagePath = this.user?.Image ?? '';
      this.photo = this.user?.Image ?? '';
      this.haveImage = !!this.user?.Image;
      this.fullName = this.user?.Name ? `${this.user.Name} ${this.user.LastName}`.trim() : '';
      this.email = this.user?.UserName ?? '';
    });
  }

  public getApplicationsMenu(): void {
    const cachedMenus = sessionStorage.getItem('menus');
    if (cachedMenus) {
      try {
        const parsed = JSON.parse(cachedMenus) as IMenuByUserResponse;
        if (parsed?.applicationMenus?.length) {
          this.responseMenusUser = parsed;
          this.setSelectedAppMenu();
        }
      } catch (e) {
        console.warn('Failed to parse cached menus from sessionStorage', e);
      }
    }

    if (!this.menuRepository) return;
    const request: IApplicationUser = {
      UserGuid: this.cookieService.get(this.env?.UserGuid ?? '')?.toUpperCase(),
    } as any;

    this.menuRepository
      .MenusByUser(request)
      .pipe(
        retryWhen((errors) => errors.pipe(take(MAX_RETRIES), delay(TIME_DELAY))),
        map((response: IMenuByUserResponse) => ({
          ...response,
          applicationMenus: response.applicationMenus.map((menu: any) => ({
            ...menu,
            modules: this.isLocalWeb ? menu.modules : menu.modules.filter((module: any) => !module.isForEdge),
          })),
        })),
        takeUntil(this._destroy$),
      )
      .subscribe((response: IMenuByUserResponse) => {
        this.responseMenusUser = { ...response };
        this.setSelectedAppMenu();
        sessionStorage.setItem('menus', JSON.stringify(this.responseMenusUser));
      });
  }

  private setSelectedAppMenu(): void {
    const applicationGuidSelected = sessionStorage.getItem('ApplicationGuid')?.toUpperCase();
    let selected = this.responseMenusUser?.applicationMenus?.find(
      (app) => app.applicationGuid?.toUpperCase() === applicationGuidSelected,
    );

    if (!selected) {
      selected = this._resolveAppFromCurrentUrl();
      if (selected) {
        sessionStorage.setItem('ApplicationGuid', selected.applicationGuid);
        if (selected.logo) sessionStorage.setItem('ApplicationLogo', selected.logo);
        if (selected.logoDark) sessionStorage.setItem('ApplicationLogoDark', selected.logoDark);
        this.setLogos();
      }
    }

    if (!selected) return;

    const addTranslations = (modules: Module[]): Module[] =>
      modules.map((module) => ({
        ...module,
        translation: module?.translates?.find((t) => t.languageCode === this.languageCode)?.value,
        children: module.children ? addTranslations(module.children) : [],
      }));

    this.applicationMenu = { ...selected, modules: addTranslations(selected.modules) } as unknown as ApplicationMenu;

    this.adjustmentOveraly();
    this._identifyCurrentPathToActiveItemMenu(this.applicationMenu);
  }

  private _resolveAppFromCurrentUrl(): any | null {
    if (!this.responseMenusUser?.applicationMenus?.length) return null;
    const currentPath = this.router.url.startsWith('/') ? this.router.url.substring(1) : this.router.url;
    const topSegment = currentPath.split('/')[0];
    let bestMatch: any = null;
    let bestCount = 0;

    for (const app of this.responseMenusUser.applicationMenus) {
      if (!app.modules) continue;
      const matchingModules = app.modules.filter((m: any) => {
        const moduleUrl = (m.url || '').replace(/^\//, '');
        return moduleUrl && moduleUrl.split('/')[0] === topSegment;
      });
      if (matchingModules.length > bestCount) {
        bestCount = matchingModules.length;
        bestMatch = app;
      }
    }
    return bestMatch;
  }

  private _identifyCurrentPathToActiveItemMenu(menuItem: ApplicationMenu): void {
    const pathname = this.router.url;
    if (!menuItem.modules) return;

    menuItem.modules.forEach((module) => {
      const isCurrentLevel1Path =
        module.url && (module.url === pathname || (pathname.includes(module.url) && module.children?.length));
      module.isActiveRoute = !!isCurrentLevel1Path;
      module.children?.forEach((child) => {
        const isCurrentLevel2Path =
          child.url && (child.url === pathname || (pathname.includes(child.url) && child.children?.length));
        child.isActiveRoute = !!isCurrentLevel2Path;
        child.children?.forEach((grandchild) => {
          grandchild.isActiveRoute = !!(grandchild.url && pathname.includes(grandchild.url));
        });
      });
    });
  }

  public adjustmentOveraly(): void {
    const count = this.responseMenusUser?.applicationMenus?.length ?? 0;
    if (count > 9) this.heightOveraly = '530px';
    else if (count > 6) this.heightOveraly = '400px';
    else if (count > 3) this.heightOveraly = '310px';
    else this.heightOveraly = '220px';
  }

  public translations(modules: Module[]): Module[] {
    modules.forEach((module) => {
      module.translation = module?.translates?.filter((t) => t.languageCode === this.languageCode)[0]?.value;
    });
    return modules;
  }

  isUnavailableOffline(module: Module): boolean {
    const urlsOffline: string[] = (this.env?.urlsUnAvailableOffline ?? '').split(',');
    return this.isLocalWeb && !this.arkikStatus?.isArkikOnline() && module.url && urlsOffline.includes(module.url);
  }

  isBackorderManagementItem(module: Module): boolean {
    return (module?.url || '').toLowerCase().includes('backordermanagement');
  }

  mouseDownModule(module: Module, event: any): void {
    event.preventDefault();
    if (module.url && (!module.children?.length || !module.showChildrenSideMenu)) {
      if (event.ctrlKey || event.detail?.ctrlKey) this.openUrlInNewTab(`${location.origin}/${module.url}`, true);
      else if (!this.router.url.startsWith('/' + module.url)) this.router.navigateByUrl(module.url);
    }
    if (module?.externalUrl?.trim()?.length) this.openNewTab(module.externalUrl);
  }

  openAppInterface(item: ApplicationMenu): void {
    sessionStorage.setItem('ApplicationGuid', item.applicationGuid);
    sessionStorage.setItem('ApplicationLogo', item.logo);

    if (item.logoDark) sessionStorage.setItem('ApplicationLogoDark', item.logoDark);
    else sessionStorage.setItem('ApplicationLogoDark', getDarkLogoSrc(item.logo));

    if (item.modules?.length) {
      let moduleUrl = '';
      let currentModule = item.modules[0];
      do {
        if (!currentModule.url && currentModule.children?.length > 0) currentModule = currentModule.children[0];
        else moduleUrl = currentModule.url;
      } while (!moduleUrl);

      this.router.navigate([`/${moduleUrl}`]).then(() => {
        if (this.Logo !== '') {
          this.setLogos();
          this.setSelectedAppMenu();
        }
      });
    }
  }

  openNewTab(url: string): void {
    const hyperlink = document.getElementById('hyperlinkExternalUrl');
    hyperlink?.setAttribute('href', url);
    hyperlink?.click();
  }

  openOptionMenu(item: Module): void {
    this.router.navigate([`/${item.url}`]);
  }

  verifyAlertOpenNewTab(item: ApplicationMenu): void {
    if (this.router.url.includes('/orders/order-taking/') || this.menuItemsDisabled) {
      this.currentItem = item;
      this.alert('warning', this.pt('views.orders.order-taking.warningOnCloseOrder'), this.openNewTab);
    } else {
      this.openNewTab(item.url);
    }
  }

  verifyAlertOpenAppInterface(item: ApplicationMenu): void {
    if (this.router.url.includes('/orders/order-taking/') || this.menuItemsDisabled) {
      this.currentItem = item;
      this.alert('warning', this.pt('views.orders.order-taking.warningOnCloseOrder'), this.openAppInterface);
    } else {
      this.openAppInterface(item);
    }
  }

  appInterfaceSelected(item: ApplicationMenu): void {
    if (item.target === 'blank') this.verifyAlertOpenNewTab(item);
    else if (item.target === 'container') this.verifyAlertOpenAppInterface(item);
  }

  menuItemSelected(event: Event, item: any): void {
    event.preventDefault();
    if (item?.url || item?.externalUrl) {
      if (item?.externalUrl?.length > 0) this.openUrlInNewTab(item.externalUrl);
      else this.router.navigate([`/${item.url}`]);
    }
  }

  changeCollapseSidebar(event: any): void {
    this.uxSideNavAdminService.setOpen(!event.detail);
  }

  changeDarkMode(event: boolean): void {
    this.darkModeService.setDarkMode(event);
  }

  changeLanguage(e: any): void {
    sessionStorage.setItem('language', e.detail.id);
    sessionStorage.setItem('languageCode', e.detail.value);
    this.ts?.lang(e.detail.pipeId);
    location.reload();
  }

  setLogos(): void {
    this.Logo = sessionStorage.getItem('ApplicationLogo');
    this.LogoDark = sessionStorage.getItem('ApplicationLogoDark');
  }

  SignOut(): void {
    if (this.env?.UserGuid) this.cookieService.delete(this.env.UserGuid);
    if (this.env?.BusinessPlanGuid) this.cookieService.delete(this.env.BusinessPlanGuid);
    if (this.env?.ProductGuid) this.cookieService.delete(this.env.ProductGuid);
    if (this.env?.CompanyGuid) this.cookieService.delete(this.env.CompanyGuid);
    this.cookieService.deleteAll();

    const b2cSignout = this.env?.B2C_signout ?? '/logout';
    let logoutRoute = '/certify/home/index?logoutcertify';

    if (this.isLocalWeb) {
      if (this.arkikStatus?.isArkikOnline()) logoutRoute = b2cSignout;
      else {
        logoutRoute = '/logout-offline';
        this.arkikStatus?.setSessionClosed();
      }
      this.authServiceB2C?.logoutRedirect({ postLogoutRedirectUri: logoutRoute });
    } else {
      this.router.navigate([logoutRoute], { queryParams: { logoutcertify: true } });
      this.cookieService.deleteAll();
      this.authServiceB2C?.logoutRedirect({ postLogoutRedirectUri: b2cSignout });
    }
  }

  alert(type: string, msg: string, callback: any = undefined): void {
    if (type === 'success') {
      this.iconAlert = 'accepted-ok';
      this.colorIconAlert = 'bright-green';
    } else if (type === 'warning') {
      this.iconAlert = 'warning-rounded-fill';
      this.colorIconAlert = 'bright-orange-dark';
    } else {
      this.iconAlert = 'error';
      this.colorIconAlert = 'true-red';
    }
    this.msgAlert = msg;
    this.cancelButtonText = this.pt('views.globals.cancel');
    this.confirmButtonText = this.pt('views.globals.confirm');
    this.callbackCloseAlertModal = callback;
    if (this.portal && this.modalAlert) {
      this.portal.components = [new TemplatePortal(this.modalAlert, this.viewContainerRef)];
    }
    this.showAlert = true;
  }

  closeAlert(): void {
    this.showAlert = false;
  }

  confirmActionModal(): void {
    if (this.callbackCloseAlertModal) this.callbackCloseAlertModal(this.currentItem);
    this.showAlert = false;
  }

  openUrlInNewTab(url: string, duplicate: boolean = false): void {
    if (duplicate) {
      const applicationData: Record<string, string> = {};
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.startsWith('Application')) applicationData[key] = sessionStorage.getItem(key);
      }
      if (Object.keys(applicationData).length) {
        const safeParamEncoded = this.arkCommon.encryptJSONObject(applicationData, this.onOpenTabKey);
        url += `?${new URLSearchParams({ prms: safeParamEncoded }).toString()}`;
      }
    }

    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
    setTimeout(() => link.remove(), 200);
  }

  onScroll(event: Event): void {
    this.cwcScrollBusService.emit(event);
  }

  get HaveMenuUsersPermission(): boolean {
    return this.haveMenuUsersPermission;
  }

  get isLocalWeb(): boolean {
    return this.env?.isLocalWeb ?? false;
  }

  async getUserRoles(): Promise<void> {
    if (!this.idb) return;
    this.userRoles = ((await this.idb.select(IDB_USER_ROLES)) as IUserRoles[]) || [];
    const rolesWithPermission = this.userRoles.map((item) => item.RoleGuid.toUpperCase());
    const menuUsersRoles: string[] = this.componentsByRole?.menu_users ?? [];
    const menuUsers = _.intersection(menuUsersRoles, rolesWithPermission);
    this.haveMenuUsersPermission = menuUsers.length > 0;
    const supportGuid = this.env?.SupportUserGuid ?? '';
    this.isSupportUser = supportGuid
      ? (this.userRoles?.some((r) => r?.RoleGuid?.toUpperCase() === supportGuid.toUpperCase()) ?? false)
      : false;
  }

  public getParams(): void {
    if (!this.parametersDataApi) return;
    const params = [{ parameterCode: 'DCPL_COM_MOD', parameterScopeTypeId: 5, scopeId: null }];
    this.parametersDataApi
      .getParametersData(params)
      .pipe(takeUntil(this._destroy$))
      .subscribe((response) => {
        try {
          const parsed: ParameterData[] = JSON.parse(response);
          this.DCPL_COM_MOD = parsed?.find((el) => el.parameterCode === 'DCPL_COM_MOD')?.parameterValue === '1';
          this.showLinks = !this.DCPL_COM_MOD;
        } catch {
          this.DCPL_COM_MOD = false;
          this.showLinks = true;
        }
      });
  }

  public GetDataApplicationModules(): void {
    if (!this.applicationRepository) return;
    const request: IApplicationModules = {
      ApplicationGuid: sessionStorage.getItem('ApplicationGuid')?.toUpperCase() || '',
    } as any;

    this.applicationRepository
      .ApplicationModules(request)
      .pipe(
        retryWhen((errors) => errors.pipe(take(MAX_RETRIES), delay(TIME_DELAY))),
        takeUntil(this._destroy$),
      )
      .subscribe((response: IApplicationModules[]) => {
        if (this.cookieService.get(this.env?.CompanyGuid ?? '') !== '00000000-0000-0000-0000-000000000000') {
          this.applicationModules = response;
        } else {
          this.applicationModules = response.filter((module) => module.isRoot);
        }
      });
  }

  public get ModulesItems(): IApplicationModules[] {
    return this.applicationModules?.map((item) => item) ?? [];
  }

  public GetDataApplications(): void {
    if (!this.applicationRepository) return;
    const request: IApplicationUser = {
      UserGuid: this.cookieService.get(this.env?.UserGuid ?? '')?.toUpperCase() || '',
    } as any;

    this.applicationRepository
      .ApplicationsByUser(request)
      .pipe(
        retryWhen((errors) => errors.pipe(take(MAX_RETRIES), delay(TIME_DELAY))),
        takeUntil(this._destroy$),
      )
      .subscribe((response: IApplication[]) => {
        this.applications = response;
        this.adjustmentOveraly();
        this.applications.forEach(({ ApplicationGuid }) => this.GetDataAllApplicationModules(ApplicationGuid));
      });
  }

  public get ApplicationItems(): IApplication[] {
    return this.applications?.map((item) => item) ?? [];
  }

  public GetDataAllApplicationModules(appUid: string): void {
    if (!this.applicationRepository) return;
    const request: IApplicationModules = { ApplicationGuid: appUid.toUpperCase() } as any;
    this.applicationRepository
      .ApplicationModules(request)
      .pipe(
        retryWhen((errors) => errors.pipe(take(MAX_RETRIES), delay(TIME_DELAY))),
        takeUntil(this._destroy$),
      )
      .subscribe((response: IApplicationModules[]) => this.allApplicationModules.set(appUid, response));
  }

  private pt(key: string): string {
    return this.ts ? this.ts.pt(key) : key;
  }
}
